import type { ReportSkillId } from "@/generated/graphql";
import { type Dictionary, type TranslationKey, useT } from "@/i18n";
import type { IconType } from "react-icons";
import { HiFlag, HiLockClosed, HiStar } from "react-icons/hi";
import { UNAVAILABLE_SKILL, skillLabel } from "./reportTokens";

interface InsightVariant {
  accent: string;
  icon: IconType;
  labelKey: TranslationKey;
}

const NEXT_FOCUS: InsightVariant = {
  accent: "#C25ED8",
  icon: HiFlag,
  labelKey: "report.insights.areaToImprove",
};
const STRONGEST: InsightVariant = {
  accent: "#3A86FF",
  icon: HiStar,
  labelKey: "report.insights.strongestSkill",
};

function InsightColumn({
  skill,
  variant,
  t,
}: {
  skill: ReportSkillId | null | undefined;
  variant: InsightVariant;
  t: Dictionary;
}) {
  const Icon = variant.icon;

  return (
    <div className="flex-1 min-w-0 text-center">
      <div className="flex items-center justify-center gap-1.5">
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: variant.accent }}
        >
          <Icon className="h-3 w-3 text-white" />
        </span>
        <span
          className="truncate text-[11px] font-extrabold uppercase tracking-wider"
          style={{ color: variant.accent }}
        >
          {t[variant.labelKey]}
        </span>
      </div>
      {skill != null ? (
        <p className="mt-1.5 truncate font-kumbh text-lg font-bold text-app-ink">
          {skillLabel(t, skill)}
        </p>
      ) : (
        // Each insight locks on its own — one scored skill names a strongest without pretending
        // to know what needs work.
        <span
          className="mt-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full"
          style={{ backgroundColor: UNAVAILABLE_SKILL.background }}
          title={t["report.insights.locked"]}
        >
          <HiLockClosed className="h-3.5 w-3.5" style={{ color: UNAVAILABLE_SKILL.color }} />
        </span>
      )}
    </div>
  );
}

/** The two skills worth naming: the weakest to work on, the strongest to lean on. */
export function InsightsCard({
  strongestSkill,
  nextFocus,
}: {
  strongestSkill: ReportSkillId | null | undefined;
  nextFocus: ReportSkillId | null | undefined;
}) {
  const t = useT();

  return (
    <div className="app-card px-5 py-4">
      <p className="app-label">{t["report.insights.title"]}</p>

      <div className="mt-3 flex items-stretch">
        <InsightColumn skill={nextFocus} variant={NEXT_FOCUS} t={t} />
        <div className="mx-2 w-px bg-gray-100" />
        <InsightColumn skill={strongestSkill} variant={STRONGEST} t={t} />
      </div>
    </div>
  );
}
